import DetectionsIcon from '../assets/Detections.jsx';
import HeatMapIcon from '../assets/Heatmap.jsx';
import HeatMapLegend from './HeatMapLegend.jsx';
import PropTypes from 'prop-types';
import React, {memo} from 'react';
import SliderFilter from './SliderFilter.jsx';
import ToggleButton from './ToggleButton.jsx';
import {
  Box,
  Divider,
  SvgIcon,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import {MODE, TIME_MODE} from '../constants/heatmap.js';

const DetectionsLegend = ({classes}) => {
  return classes.map(cls => (
    <Box key={cls.name} display="flex" alignItems="center">
      <Box
        minWidth={16}
        maxWidth={16}
        minHeight={16}
        maxHeight={16}
        borderRadius={4}
        bgcolor={cls.color}
        marginRight={0.5}
        marginLeft={1.5}
      >
        &nbsp;
      </Box>
      <Box>
        <Typography>{cls.name}</Typography>
      </Box>
    </Box>
  ));
};

const StyledToolbar = styled(Toolbar)(({theme}) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.primary.contrastText,
}));

const ScoreText = styled(Typography)(({theme, disabled}) => ({
  color: disabled ? theme.palette.grey[400] : undefined,
}));

const StyledDivider = styled(Divider)(({theme}) => ({
  backgroundColor: theme.palette.grey[300],
  height: 40,
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
}));

const StyledText = styled(Typography)(({theme}) => ({
  marginRight: theme.spacing(2),
}));

/**
 * The map toolbar is the toolbar on the top of the main collection map.
 * It allows switching between detections and heatmap.
 * It has a time toggle for showing either a specific layer or all time periods.
 * It also has the confidence score filtering if the data has scores.
 * And it allows going into the Change View by drawing a box.
 */
const MapToolbar = ({
  children,
  disabled,
  handleFilterChange,
  filterValues,
  gradient,
  onGradientChange,
  defaultFilterValues,
  filterDisabled,
  mode,
  onModeChange,
  onTimeModeChange,
  timeMode,
  classes,
}) => {
  return (
    <StyledToolbar>
      <StyledText>Visualization</StyledText>
      <Box>
        <ToggleButton
          onClick={() => onModeChange(MODE.HEATMAP)}
          title="Show Heatmap"
          selected={mode === MODE.HEATMAP}
          disabled={disabled}
        >
          <SvgIcon>
            <HeatMapIcon />
          </SvgIcon>
        </ToggleButton>
        <ToggleButton
          title="Show Detections"
          onClick={() => onModeChange(MODE.DETECTIONS)}
          selected={mode === MODE.DETECTIONS}
          disabled={disabled}
        >
          <SvgIcon>
            <DetectionsIcon />
          </SvgIcon>
        </ToggleButton>
      </Box>
      <StyledDivider orientation="vertical" />
      <Typography>Show all time</Typography>
      <Switch
        checked={timeMode === TIME_MODE.ALL}
        color="primary"
        onChange={onTimeModeChange}
      />
      <StyledDivider orientation="vertical" />
      <ScoreText disabled={filterDisabled}>Confidence</ScoreText>
      <Tooltip title="Confidence Score Filter" position="right">
        <Box
          display="flex"
          flexGrow={1}
          alignItems="center"
          pl={2}
          pr={1}
          maxWidth={250}
        >
          <SliderFilter
            min={0}
            max={1}
            step={0.1}
            size="small"
            initialValue={filterValues.confidence}
            defaultValue={defaultFilterValues.confidence}
            disabled={filterDisabled}
            id="confidence"
            onChangeCommitted={handleFilterChange}
            valueLabelDisplay="auto"
            valueLabelFormat={value => `${value * 100}%`}
            marks={[
              {
                value: 0,
                label: '0%',
              },
              {
                value: 1,
                label: '100%',
              },
            ]}
          />
        </Box>
      </Tooltip>
      {mode === MODE.HEATMAP ? (
        <>
          <StyledDivider orientation="vertical" />
          <HeatMapLegend gradient={gradient} onClick={onGradientChange} />
        </>
      ) : (
        classes?.length > 0 && (
          <>
            <StyledDivider orientation="vertical" />
            <DetectionsLegend classes={classes} />
          </>
        )
      )}
      {!!children && (
        <>
          <StyledDivider orientation="vertical" />
          {children}
        </>
      )}
    </StyledToolbar>
  );
};

MapToolbar.propTypes = {
  children: PropTypes.any,
  defaultFilterValues: PropTypes.object,
  disabled: PropTypes.bool,
  filterDisabled: PropTypes.bool,
  filterValues: PropTypes.object,
  gradient: PropTypes.array,
  handleFilterChange: PropTypes.func,
  mode: PropTypes.string,
  onGradientChange: PropTypes.func,
  onModeChange: PropTypes.func,
  onTimeModeChange: PropTypes.func,
  timeMode: PropTypes.string,
  classes: PropTypes.array,
};

export default memo(MapToolbar);
